import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import OllieIcon from 'src/components/icons/OllieIcon'
import OllieOrderingComposer from 'src/components/ollie/OllieOrderingComposer'
import OllieOrderingProposal from 'src/components/ollie/OllieOrderingProposal'
import OlliePaywallModal from 'src/components/ollie/OlliePaywallModal'
import { useCanAccess } from 'src/hooks/useIsBlocked'
import useOllieOrderingQuantityEdits from 'src/hooks/useOllieOrderingQuantityEdits'
import useOllieOrderingSession from 'src/hooks/useOllieOrderingSession'
import { canAccess } from 'src/utils/permissions'
import {
  ollieOrderingConfirmNotice,
  ollieOrderingUsageWarning,
} from 'src/utils/ollieOrderingProposal'
import {
  consumeOllieOrderingHandoff,
  parseOllieOrderQuery,
  stripOllieOrderSearch,
} from 'src/utils/ollieOrderingHandoff'
import styled from 'styled-components/macro'

export const OLLIE_ORDERING_ENTRY_TITLE = 'Build my order with Ollie'
/**
 * Short promise on the card, reassurance inside the panel where the chef is
 * about to order. Same split as the app so both read the same.
 */
export const OLLIE_ORDERING_ENTRY_SUBTITLE = 'Type or say what you need.'
export const OLLIE_ORDERING_ENTRY_NOTE =
  'Ollie fills your basket from your shopping list. You still place the supplier order.'
export const OLLIE_ORDERING_ENTRY_CTA = 'Build my order'

export function canShowOllieOrderingEntry(role, customRoles) {
  return canAccess('shopping-lists', role, customRoles)
}

/**
 * Prominent Shopping Lists entry for Ollie ordering.
 * Hidden without Order permission. Does not open the generic Ollie FAB.
 */
const OllieOrderingEntry = ({ canOrder, venueId } = {}) => {
  const allowedByRole = useCanAccess('shopping-lists')
  const allowed = typeof canOrder === 'boolean' ? canOrder : allowedByRole
  const location = useLocation()
  const history = useHistory()
  const fromQuery = parseOllieOrderQuery(location.search)
  const [started, setStarted] = useState(false)
  const [seed, setSeed] = useState('')
  const [seedKey, setSeedKey] = useState(0)
  const {
    proposal,
    sending,
    confirming,
    error,
    send,
    pickListCandidate,
    editProposalLine,
    confirmProposal,
    limit,
    usage,
    confirmNotice,
    openBasket,
    dismissPaywall,
  } = useOllieOrderingSession(venueId)
  const quantityEdits = useOllieOrderingQuantityEdits({
    onEditQuantity: editProposalLine,
  })

  // A stepper click made a moment ago is still settling: send it before the
  // basket write so the chef gets the number they are looking at.
  const handleConfirm = async () => {
    await quantityEdits.flush()
    return confirmProposal()
  }

  const usageWarning = ollieOrderingUsageWarning(usage)

  useEffect(() => {
    const stored = consumeOllieOrderingHandoff(venueId)
    if (!fromQuery && !stored.open) return

    setStarted(true)
    if (stored.draft) {
      setSeed(stored.draft)
      setSeedKey(value => value + 1)
    }
    if (!fromQuery) return

    history.replace({
      pathname: location.pathname,
      search: stripOllieOrderSearch(location.search),
      hash: location.hash,
    })
  }, [
    fromQuery,
    history,
    location.hash,
    location.pathname,
    location.search,
    venueId,
  ])

  if (!allowed) {
    return null
  }

  return (
    <Card
      data-testid='ollie-ordering-entry'
      role='region'
      aria-label={OLLIE_ORDERING_ENTRY_TITLE}
    >
      <Header>
        <IconBadge aria-hidden='true'>
          <OllieIcon size={28} variant='inverse' />
        </IconBadge>
        <Copy>
          <Title>{OLLIE_ORDERING_ENTRY_TITLE}</Title>
          <Subtitle>{OLLIE_ORDERING_ENTRY_SUBTITLE}</Subtitle>
        </Copy>
        {started ? (
          <TextButton
            type='button'
            onClick={() => setStarted(false)}
            data-testid='ollie-ordering-close'
          >
            Close
          </TextButton>
        ) : (
          <StartButton
            type='button'
            onClick={() => setStarted(true)}
            data-testid='ollie-ordering-start'
          >
            {OLLIE_ORDERING_ENTRY_CTA}
          </StartButton>
        )}
      </Header>
      {started ? (
        <Panel
          data-testid='ollie-ordering-panel'
          aria-label={OLLIE_ORDERING_ENTRY_TITLE}
        >
          <Note>{OLLIE_ORDERING_ENTRY_NOTE}</Note>
          <OllieOrderingComposer
            key={seedKey}
            sending={sending}
            disabled={confirming}
            venueId={venueId}
            onSend={send}
            initialDraft={seed}
            autoSendDraft={Boolean(seed)}
          />
          {usageWarning && !limit ? (
            <UsageWarning data-testid='ollie-ordering-usage-warning'>
              {usageWarning}
            </UsageWarning>
          ) : null}
          {confirmNotice ? (
            <ConfirmNotice data-testid='ollie-ordering-confirm-notice'>
              <span>{ollieOrderingConfirmNotice(confirmNotice)}</span>
              <TextButton
                type='button'
                onClick={openBasket}
                data-testid='ollie-ordering-view-basket'
              >
                View your basket
              </TextButton>
            </ConfirmNotice>
          ) : null}
          {error ? (
            <ErrorMessage data-testid='ollie-ordering-error'>
              {error}
            </ErrorMessage>
          ) : null}
          <OllieOrderingProposal
            proposal={proposal}
            picking={sending}
            confirming={confirming}
            quantityEdits={quantityEdits}
            onPickListCandidate={pickListCandidate}
            onEditQuantity={editProposalLine}
            onRemoveLine={({ phrase }) =>
              editProposalLine({ phrase, remove: true })
            }
            onConfirm={handleConfirm}
          />
        </Panel>
      ) : null}
      <OlliePaywallModal
        visible={Boolean(limit)}
        paywall={limit?.paywall}
        usage={limit?.usage}
        limitType={limit?.limitType}
        venueId={venueId}
        onClose={dismissPaywall}
      />
    </Card>
  )
}

const Card = styled.section`
  background: ${({ theme }) => theme.colours.mayoWhite};
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-left: 6px solid ${({ theme }) => theme.colours.lettuceGreen};
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(19, 45, 7, 0.08);
  box-sizing: border-box;
  margin: 0 0 24px;
  max-width: none;
  padding: 20px 22px;
  text-align: left;
  width: 100%;
`

const Header = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`

const IconBadge = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colours.lettuceGreen};
  border-radius: 50%;
  display: flex;
  flex-shrink: 0;
  height: 48px;
  justify-content: center;
  width: 48px;
`

const Copy = styled.div`
  display: flex;
  flex: 1 1 220px;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

const Title = styled.h2`
  color: ${({ theme }) => theme.colours.seaweedGreen};
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
`

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 15px;
  line-height: 1.45;
  margin: 0;
`

const StartButton = styled.button`
  background: ${({ theme }) => theme.colours.lettuceGreen};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colours.seaweedGreen};
  cursor: pointer;
  flex-shrink: 0;
  font-size: 15px;
  font-weight: 700;
  padding: 10px 18px;

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colours.avocadoGreen};
  }
`

const TextButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colours.oliveGrey};
  cursor: pointer;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 600;
  padding: 8px;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colours.bbqBlack};
  }
`

const Panel = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colours.panSilver};
  margin-top: 16px;
  padding-top: 16px;
`

const Note = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 14px;
  line-height: 1.45;
  margin: 0 0 12px;
`

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colours.chilliRed || '#ff4d4f'};
  font-size: 14px;
  margin: 10px 0 0;
`

const UsageWarning = styled.p`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 13px;
  margin: 8px 0 0;
`

const ConfirmNotice = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colours.lightGreen || '#f0f6ef'};
  border-radius: 8px;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  margin: 10px 0 0;
  padding: 10px 12px;

  span {
    font-size: 14px;
    line-height: 1.45;
  }
`

export default OllieOrderingEntry
